<template>
  <div :class="['message-bubble-container', { 'outgoing': isOutgoing, 'incoming': !isOutgoing }]">
    <!-- Mensaje entrante (izquierda) -->
    <div v-if="!isOutgoing" class="message-wrapper incoming">
      <div class="message-bubble incoming-bubble">
        <!-- Contenido del mensaje -->
        <div class="message-content">
          <div v-if="message.content" class="message-text">
            {{ message.content }}
          </div>
        </div>
        
        <!-- Metadata del mensaje -->
        <div class="message-meta">
          <span class="message-time">
            {{ formatTime(message.created_at) }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Mensaje saliente (derecha) -->
    <div v-else class="message-wrapper outgoing">
      <div class="message-bubble outgoing-bubble">
        <!-- Contenido del mensaje -->
        <div class="message-content">
          <div v-if="message.content" class="message-text">
            {{ message.content }}
          </div>
        </div>

        <!-- Metadata del mensaje -->
        <div class="message-meta">
          <span class="message-time">
            {{ formatTime(message.created_at) }}
          </span>
          
          <!-- Indicador de estado del mensaje -->
          <div class="message-status ml-1">
            <v-icon 
              :color="statusColor" 
              size="14"
              :class="{ 'status-read': messageStatus === 'read' }"
            >
              {{ statusIcon }}
            </v-icon>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Menú contextual (aparece al hover) -->
    <div v-if="showContextMenu" class="context-menu-trigger">
      <v-menu>
        <template #activator="{ props }">
          <v-btn
            icon="mdi-chevron-down"
            variant="text"
            size="x-small"
            v-bind="props"
            class="context-menu-btn"
          />
        </template>
        <v-list density="compact">
          <v-list-item @click="copyMessage">
            <template #prepend>
              <v-icon size="small">mdi-content-copy</v-icon>
            </template>
            <v-list-item-title>Copiar mensaje</v-list-item-title>
          </v-list-item>
          <v-list-item @click="replyToMessage">
            <template #prepend>
              <v-icon size="small">mdi-reply</v-icon>
            </template>
            <v-list-item-title>Responder</v-list-item-title>
          </v-list-item>
          <v-list-item @click="forwardMessage">
            <template #prepend>
              <v-icon size="small">mdi-share</v-icon>
            </template>
            <v-list-item-title>Reenviar</v-list-item-title>
          </v-list-item>
          <v-divider v-if="isOutgoing" />
          <v-list-item v-if="isOutgoing && canDeleteMessage" @click="deleteMessage" class="text-error">
            <template #prepend>
              <v-icon size="small" color="error">mdi-delete</v-icon>
            </template>
            <v-list-item-title>Eliminar mensaje</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  isOutgoing: {
    type: Boolean,
    default: false
  },
  patientName: {
    type: String,
    default: ''
  },
  showContextMenu: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['reply', 'forward', 'delete'])

// Computed
const messageStatus = computed(() => {
  const raw = props.message.twilio_status || props.message.status || props.message.read_status || 'sent'
  // Mapear estados de Twilio a estados del UI
  const statusMap = {
    queued: 'sending',
    sending: 'sending',
    sent: 'sent',
    delivered: 'delivered',
    read: 'read',
    failed: 'failed',
    undelivered: 'failed'
  }
  return statusMap[raw] || raw
})

const statusColor = computed(() => {
  const colors = {
    sending: 'orange-lighten-1',
    sent: 'grey-lighten-1',
    delivered: 'grey-lighten-1',
    read: 'blue-lighten-1',
    failed: 'error'
  }
  return colors[messageStatus.value] || 'grey-lighten-1'
})

const statusIcon = computed(() => {
  const icons = {
    sending: 'mdi-clock-outline',
    sent: 'mdi-check',
    delivered: 'mdi-check-all',
    read: 'mdi-check-all',
    failed: 'mdi-alert-circle'
  }
  return icons[messageStatus.value] || 'mdi-check'
})

const canDeleteMessage = computed(() => {
  if (!props.isOutgoing) return false
  
  // Solo permitir eliminar mensajes de menos de 1 hora
  const messageTime = new Date(props.message.created_at)
  const now = new Date()
  const hoursDiff = (now - messageTime) / (1000 * 60 * 60)
  
  return hoursDiff < 1
})

// Methods
const formatTime = (timestamp) => {
  if (!timestamp) return ''
  
  return new Date(timestamp).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const copyMessage = async () => {
  try {
    await navigator.clipboard.writeText(props.message.content)
  } catch (error) {
    console.error('Error copiando mensaje:', error)
  }
}

const replyToMessage = () => {
  emit('reply', props.message)
}

const forwardMessage = () => {
  emit('forward', props.message)
}

const deleteMessage = () => {
  if (canDeleteMessage.value) {
    emit('delete', props.message)
  }
}
</script>

<style scoped>
.message-bubble-container {
  margin-bottom: 8px;
  position: relative;
}

.message-wrapper {
  display: flex;
  max-width: 85%;
  position: relative;
}

.message-wrapper.incoming {
  justify-content: flex-start;
  margin-right: auto;
}

.message-wrapper.outgoing {
  justify-content: flex-end;
  margin-left: auto;
}

.message-bubble {
  border-radius: 18px;
  padding: 8px 12px;
  word-wrap: break-word;
  word-break: break-word;
  position: relative;
  max-width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.incoming-bubble {
  background: #ffffff;
  color: #1f1f1f;
  border-bottom-left-radius: 4px;
  border: 1px solid #e0e0e0;
}

.outgoing-bubble {
  background: #dcf8c6;
  color: #1f1f1f;
  border-bottom-right-radius: 4px;
}

.message-content {
  line-height: 1.4;
}

.message-text {
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  gap: 4px;
}

.message-time {
  font-size: 11px;
  color: #667781;
  opacity: 0.8;
  margin-top: 2px;
}

.message-status {
  display: flex;
  align-items: center;
}

.status-read {
  color: #4fc3f7 !important;
}

.context-menu-trigger {
  position: absolute;
  top: -8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-bubble-container:hover .context-menu-trigger {
  opacity: 1;
}

.context-menu-btn {
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Animaciones */
.message-bubble-container {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .message-wrapper {
    max-width: 90%;
  }
  
  .message-bubble {
    padding: 6px 10px;
  }
  
  .message-text {
    font-size: 13px;
  }
}

/* Estados de mensaje especiales */
.message-bubble.failed {
  background: #ffebee !important;
  border: 1px solid #ef5350;
}

.message-bubble.sending {
  opacity: 0.7;
}

.message-bubble.failed .message-text {
  color: #c62828;
}

/* Indicador de mensaje largo */
.message-text.long-message {
  max-height: 200px;
  overflow-y: auto;
}

.message-text.long-message::-webkit-scrollbar {
  width: 4px;
}

.message-text.long-message::-webkit-scrollbar-track {
  background: transparent;
}

.message-text.long-message::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}
</style>